import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import { OrderSideEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getCurrentAccount } from '../../lib/store/crm/helpers';
import {
  getCompanyLogoAndName as getLogoAndName,
  loadStock as serviceLoadStock,
} from '../../lib/store/fundamental';
import { ChartDataRange } from '../../lib/store/market-data/charting/types';
import { getAccountTradeHistory, getOpenPositions } from '../../lib/store/reporting';
import {
  PENDING_ORDERS_REQUEST_TENNSTATUS,
  TRADE_HISTORY_ORDERS_TENNSTATUSES,
} from '../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData, OpenPosition } from '../../lib/store/reporting/types';
import {
  hasValidCookie,
  isSymbolETF as isETF,
} from '../../lib/store/selectors';
import { getBuyingPower } from '../../lib/store/trading';
import BigChartCache from '../../lib/store-util/BigChartCache';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import SymbolStatusCache from '../../lib/store-util/SymbolStatusCache';
import { checkIfSymbolIsHalted } from '../../lib/util/HaltSymbolHelper';
import { isMarketRegularHours } from '../../lib/util/MarketDataHelpers';
import { isEmpty } from '../../lib/util/typeGuards';

import {
  GoBackButton,
  Grid,
  LatestNews,
  OrderNavigation,
  ShortSellOrderPlace,
  SymbolDetailsHeader,
  TradingButtons,
} from '../../components';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import CreateOrderModal from '../../components/OrderActions/CreateOrder/CreateOrderModal';
import OrderResponseManager from '../../components/OrderResponseManager/OrderResponseManager';
import { useIsMobile, useMaintenanceMode, useTradingButtonsDisabled } from '../../hooks';
import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';

import { Financials, Overview, Ratios } from './Tabs';

import styles from './SymbolDetails.module.scss';

type Props = ReduxProps;

const SymbolDetails = (props: Props) => {
  const {
    accountReferenceId,
    openPositions,
    transactions,
    pendingOrders,
    tradeHistoryOrders,
    companyLogoAndName,
    isSymbolETF,
    loadStock,
    getOrders,
    getPositions,
    getCompanyLogoAndName,
    fetchBuyingPowerAndPutGlobalState,
    accountStatusData,
  } = props;
  const { isMobile, isTablet } = useIsMobile();
  const { name } = useParams<'name'>();
  const symbol = name!;
  const hasCookieSession = useSelector<RootState, boolean>((state: RootState) => hasValidCookie(state));

  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [visibleWarningShortSellDialog, setVisibleWarningShortSellDialog] = useState<boolean>(false);
  const [openOrderModal, setOpenOrderModal] = useState<{ target: string; isModalOpened: boolean }>({
    target: '',
    isModalOpened: false,
  });
  const [currentRange, setCurrentRange] = useState<ChartDataRange>('1D');

  const positionsBySymbol = openPositions.find(
    (position: OpenPosition) => position.symbol === symbol,
  );
  const symbolsStatusCache = SymbolStatusCache.use(symbol);
  const isSymbolHalted = checkIfSymbolIsHalted(symbolsStatusCache);
  const isMaintenance = useMaintenanceMode();
  const navigate = useNavigate();
  const location = useLocation();
  const isButtonsDisabled = useTradingButtonsDisabled({ symbol, accountStatusData });

  useEffect(() => {
    if (accountReferenceId) {
      fetchBuyingPowerAndPutGlobalState();
    }
  }, [accountReferenceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const companyInfo = companyLogoAndName[symbol];
  const mappedSymbols = companyInfo
    ? [
      {
        symbol,
        stockExchangeCode: companyInfo.stockExchangeCode,
        countryDesignation: companyInfo.countryDesignation,
      },
    ]
    : [];

  useSubscriptionManager({
    symbols: mappedSymbols,
    currentRange,
    isBigChart: true,
    enabled: !!symbol && hasCookieSession && !!companyInfo,
  });

  useEffect(() => {
    MarketStateCache.refresh();
  }, [symbol]);

  useEffect(() => {
    BigChartCache.clear();
    setCurrentRange('1D');
  }, [symbol]);

  useEffect(() => {
    loadStock(symbol);
  }, [symbol, loadStock]);

  useEffect(() => {
    if (!companyLogoAndName[symbol]) {
      getCompanyLogoAndName(symbol);
    }
  }, [companyLogoAndName, getCompanyLogoAndName, symbol]);

  useEffect(() => {
    if (!isInitialised) {
      setIsInitialised(true);
    }
  }, [isInitialised]);

  useEffect(() => {
    if (!openPositions.find((position) => position.symbol === symbol)) {
      getPositions(symbol);
    }
    if (!pendingOrders.find((order) => order.symbol === symbol)) {
      getOrders({
        symbols_StringList: symbol,
        tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
        isPendingOrdersCall: true,
      });
    }
    if (!tradeHistoryOrders.find((order) => order.symbol === symbol)) {
      getOrders({
        symbols_StringList: symbol,
        tenNStatuses_StringList: TRADE_HISTORY_ORDERS_TENNSTATUSES,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const reviewOrder = (target: string) => {
    isMobile
      && navigate(
        {
          pathname: `/${target.includes(OrderSideEnum.Buy) ? OrderSideEnum.Buy : OrderSideEnum.Sell}/${symbol}`,
        },
        {
          state: { stateSymbol: symbol, stateTarget: target, origin: location.pathname },
          replace: true,
        },
      );

    setOpenOrderModal({ target, isModalOpened: true });
  };

  const handleCreateOrderSellOrBuy = (target: string, _isHeaderBtn?: boolean) => {
    if (isMaintenance) {
      return;
    }

    if (
      isMobile
      && !positionsBySymbol
      && isMarketRegularHours(null)
      && target === OrderSideEnum.Sell
    ) {
      setVisibleWarningShortSellDialog(true);
      return;
    }

    reviewOrder(target);
  };

  const proceedShortSellOrderPlaceModal = () => {
    setVisibleWarningShortSellDialog(false);
    reviewOrder(OrderSideEnum.SellShort);
  };

  const locationState = location.state as { symbolDetailsTab?: number } | null;
  const initialActiveTab = locationState?.symbolDetailsTab ?? 0;

  const tabs = [
    {
      text: lang.symbolDetailsTabOverview(),
      component: (
        <Overview symbol={symbol} currentRange={currentRange} setCurrentRange={setCurrentRange} />
      ),
    },
    {
      text: lang.symbolDetailsTabNews(),
      component: <LatestNews isExtended selectedSymbol={symbol} />,
    },
  ];

  if (!isSymbolETF(symbol)) {
    tabs.push({
      text: lang.symbolDetailsTabFinancials(),
      component: <Financials symbol={symbol} />,
    });
  }

  tabs.push({
    text: lang.symbolDetailsTabRatios(),
    component: <Ratios symbol={symbol} />,
  });

  return (
    <Grid
      extraChildren={(
        <>
          {!isMobile && !isTablet && (
            <div className={styles.wrapperNavigation}>
              <OrderNavigation symbol={symbol} />
            </div>
          )}
        </>
      )}
    >
      {!isMobile && (
        <div className={styles.backButton}>
          <GoBackButton />
        </div>
      )}
      <OrderResponseManager />
      {visibleWarningShortSellDialog && !positionsBySymbol && (
        <ShortSellOrderPlace
          processModal={proceedShortSellOrderPlaceModal}
          toggleModal={() => {
            setVisibleWarningShortSellDialog(false);
          }}
        />
      )}
      {!isMobile && !isEmpty(transactions) && openOrderModal.isModalOpened && (
        <CreateOrderModal symbol={symbol} openOrderModal={openOrderModal} />
      )}
      <SymbolDetailsHeader
        symbol={symbol}
        currentRange={currentRange}
        handleCreateOrderSellOrBuy={handleCreateOrderSellOrBuy}
      />
      <div className={classNames(styles.navigationBar, { [styles.navigationBarMob]: isMobile })}>
        <NavButtons
          initialActiveTab={initialActiveTab}
          persistTabState="symbolDetailsTab"
          replaceTabState
          tabs={tabs}
        />
      </div>
      {
        isMobile
        && !isSymbolHalted
        && (
          <TradingButtons
            isButtonsDisabled={isButtonsDisabled}
            onSellClicked={() => handleCreateOrderSellOrBuy(OrderSideEnum.Sell, true)}
            onBuyClicked={() => handleCreateOrderSellOrBuy(OrderSideEnum.Buy, true)}
          />
        )
      }
    </Grid>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountReferenceId: getCurrentAccount(state),
  companyLogoAndName: state.fundamental.companyLogoAndName,
  openPositions: state.reporting.openPositions,
  pendingOrders: state.reporting.pendingOrders,
  tradeHistoryOrders: state.reporting.tradeHistoryOrders,
  isSymbolETF: (symbol: string) => isETF(symbol, state),
  transactions: state.payment.paymentsAll.data,
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  loadStock: (symbol: string) => dispatch(serviceLoadStock({ symbol })),
  getCompanyLogoAndName: (symbol: string) => dispatch(getLogoAndName(symbol)),
  getPositions: (symbol: string) => dispatch(getOpenPositions({ symbol, group: 'symbol-details' })),
  getOrders: (data: AccountTradeHistoryPayloadData) => {
    dispatch(getAccountTradeHistory({ ...data, isSymbolDetails: true }));
  },
  fetchBuyingPowerAndPutGlobalState: () => dispatch(getBuyingPower()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SymbolDetails);
